import { useEffect, useState, type FormEvent } from "react";
import { api, ApiError } from "../../api/client";
import { Modal } from "../../components/ui/Modal";
import type { AccessRequest, UnlinkedMember } from "../../types";

interface Props {
  request: AccessRequest;
  onClose: () => void;
  onApproved: () => void;
}

const NEW_MEMBER = "new";

/** Sucht unter den noch unverknüpften Mitgliedern einen Namenstreffer, um die
 *  Auswahl sinnvoll vorzubelegen - der Admin kann das jederzeit überschreiben,
 *  siehe Kommentar in utils/memberSync.ts (Backend) zum Hintergrund. */
function findExactMatch(members: UnlinkedMember[], fullName: string): UnlinkedMember | undefined {
  const normalized = fullName.trim().toLowerCase();
  return members.find((m) => `${m.first_name} ${m.last_name}`.trim().toLowerCase() === normalized);
}

/** Schlägt aus dem vollständigen Namen einen Benutzernamen vor (z.B. "Max Mustermann" -> "max.mustermann"). */
function suggestUsername(fullName: string): string {
  return fullName
    .trim()
    .toLowerCase()
    .replace(/ä/g, "ae").replace(/ö/g, "oe").replace(/ü/g, "ue").replace(/ß/g, "ss")
    .normalize("NFD").replace(/[̀-ͯ]/g, "") // sonstige Akzente entfernen (z.B. é -> e)
    .replace(/[^a-z0-9\s.]/g, "")
    .trim()
    .split(/\s+/)
    .filter(Boolean)
    .join(".");
}

/**
 * Genehmigungsdialog: legt bei Bestätigung automatisch einen neuen Benutzer
 * mit der beantragten Rolle an (Passwort wird serverseitig gehasht
 * gespeichert, siehe backend/src/routes/requests.ts).
 */
export function ApproveRequestModal({ request, onClose, onApproved }: Props) {
  const [username, setUsername] = useState(suggestUsername(request.full_name));
  const [password, setPassword] = useState("");
  const [passwordRepeat, setPasswordRepeat] = useState("");
  const [unlinkedMembers, setUnlinkedMembers] = useState<UnlinkedMember[]>([]);
  const [linkMemberId, setLinkMemberId] = useState<string>(NEW_MEMBER);
  const [error, setError] = useState<string | null>(null);
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    api.get<UnlinkedMember[]>("/api/requests/unlinked-members").then((members) => {
      setUnlinkedMembers(members);
      const match = findExactMatch(members, request.full_name);
      if (match) setLinkMemberId(String(match.id));
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  async function handleSubmit(e: FormEvent) {
    e.preventDefault();
    setError(null);
    if (password !== passwordRepeat) {
      setError("Die Passwörter stimmen nicht überein.");
      return;
    }
    setSubmitting(true);
    try {
      await api.post(`/api/requests/${request.id}/approve`, {
        username, password, passwordRepeat,
        linkMemberId: linkMemberId === NEW_MEMBER ? null : Number(linkMemberId),
      });
      onApproved();
    } catch (err) {
      setError(err instanceof ApiError ? err.message : "Genehmigung fehlgeschlagen.");
    } finally {
      setSubmitting(false);
    }
  }

  return (
    <Modal
      title={`Anfrage genehmigen: ${request.full_name}`}
      onClose={onClose}
      footer={
        <>
          <button className="btn-secondary" onClick={onClose}>Abbrechen</button>
          <button className="btn-primary" form="approve-request-form" disabled={submitting}>
            {submitting ? "Wird angelegt…" : "Genehmigen & Benutzer anlegen"}
          </button>
        </>
      }
    >
      <form id="approve-request-form" onSubmit={handleSubmit} className="space-y-4">
        {error && <div className="bg-danger-muted text-danger text-sm rounded-lg px-3 py-2">{error}</div>}

        <p className="text-sm text-gray-500">
          Legt einen Benutzer für <span className="text-gray-300 font-medium">{request.full_name}</span> mit der
          Rolle <span className="text-gray-300 font-medium">{request.requested_role_name}</span> an.
        </p>

        <div>
          <label className="label">Benutzername *</label>
          <input className="input" value={username} onChange={(e) => setUsername(e.target.value)} minLength={3} required />
        </div>
        <div>
          <label className="label">Passwort *</label>
          <input className="input" type="password" value={password} onChange={(e) => setPassword(e.target.value)} minLength={8} required />
        </div>
        <div>
          <label className="label">Passwort wiederholen *</label>
          <input className="input" type="password" value={passwordRepeat} onChange={(e) => setPasswordRepeat(e.target.value)} minLength={8} required />
        </div>
        <div>
          <label className="label">Mitglied verknüpfen</label>
          <select className="input" value={linkMemberId} onChange={(e) => setLinkMemberId(e.target.value)}>
            <option value={NEW_MEMBER}>— Neues Mitglied anlegen —</option>
            {unlinkedMembers.map((m) => (
              <option key={m.id} value={m.id}>
                {m.first_name} {m.last_name}{m.rank ? ` (${m.rank})` : ""}
              </option>
            ))}
          </select>
          <p className="text-xs text-gray-500 mt-1">
            Ist {request.full_name} bereits als Mitglied erfasst, hier auswählen - verhindert doppelte Einträge.
          </p>
        </div>
      </form>
    </Modal>
  );
}
